'use client';

import { useState, useCallback, useEffect, useRef, type ReactElement } from 'react';
import Image from 'next/image';
import { defineMessage, FormattedMessage, useIntl } from 'react-intl';
import { Typography, TextField, LoadingButton, Stack, LockLineIcon } from '@eventbrite/marmalade';

import type { ProtectedEventInfo } from '@/pages/listingPageTypes';
import type { Organizer } from '@/types';
import { EventDateTime } from '@/components/EventDetails/EventDateTime/EventDateTime';
import { OrganizerInfo } from '@/components/EventDetails/OrganizerInfo/OrganizerInfo';
import { SignalBadge } from '@/components/_shared/SignalBadge';
import { DisplayStatusProvider } from '@/components/_shared/contexts/DisplayStatusContext';
import { OrganizerFollowStatusProvider } from '@/components/_shared/contexts/FollowOrganizerContext';
import { OrganizerProfileProvider } from '@/components/_shared/contexts/OrganizerProfileContext';
import { withContactOrganizerQuery } from '@/utils/organizerContactUrl';

import styles from './ProtectedEventGate.module.scss';

const VERIFY_API_PATH = '/e/api';
const PASSWORD_QUERY_PARAM = 'password';
const WRONG_PASSWORD_MESSAGE = defineMessage({
  id: 'Wrong password, try again.',
  defaultMessage: 'Wrong password, try again.',
  description: 'Error shown when a wrong password is submitted on the protected event gate',
});
const GENERIC_PASSWORD_ERROR_MESSAGE = defineMessage({
  id: 'Unable to verify password. Please try again.',
  defaultMessage: 'Unable to verify password. Please try again.',
  description: 'Error shown when password verification fails for non-auth reasons on the protected event gate',
});
const PASSWORD_LABEL = defineMessage({
  id: 'Password',
  defaultMessage: 'Password',
  description: 'Password field label on protected event gate',
});

type PasswordErrorKey = 'wrongPassword' | 'generic' | null;

export type ProtectedEventGateProps = {
  event: ProtectedEventInfo;
};

function buildProtectedRedirectUrl(slug: string, search: string, hash: string): string {
  const params = new URLSearchParams(search);
  params.delete(PASSWORD_QUERY_PARAM);

  const queryString = params.toString();
  return `/e/${slug}/protected${queryString ? `?${queryString}` : ''}${hash}`;
}

export function ProtectedEventGate({ event }: ProtectedEventGateProps): ReactElement {
  const { eventId, slug, name, startDate, endDate, hideStartDate, locale, organizer, backgroundImageUrl, isEnded } =
    event;

  const { formatMessage } = useIntl();

  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordErrorKey, setPasswordErrorKey] = useState<PasswordErrorKey>(null);
  const hasAttemptedQueryPasswordRef = useRef(false);

  const verifyPassword = useCallback(
    async (candidatePassword: string) => {
      const trimmedPassword = candidatePassword.trim();
      if (!trimmedPassword) return;

      setIsSubmitting(true);
      setPasswordErrorKey(null);

      try {
        const res = await fetch(`${VERIFY_API_PATH}/${eventId}/verify-password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ password: trimmedPassword }),
        });
        if (res.ok) {
          window.location.href = buildProtectedRedirectUrl(slug, window.location.search, window.location.hash);
          return;
        }
        setPasswordErrorKey(res.status === 401 ? 'wrongPassword' : 'generic');
        setPassword('');
        setIsSubmitting(false);
      } catch {
        setPasswordErrorKey('generic');
        setPassword('');
        setIsSubmitting(false);
      }
    },
    [eventId, slug],
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      await verifyPassword(password);
    },
    [password, verifyPassword],
  );

  useEffect(() => {
    if (hasAttemptedQueryPasswordRef.current) return;

    const queryPassword = new URLSearchParams(window.location.search).get(PASSWORD_QUERY_PARAM);
    if (!queryPassword?.trim()) return;

    hasAttemptedQueryPasswordRef.current = true;
    void verifyPassword(queryPassword);
  }, [verifyPassword]);

  const organizerUrl = organizer?.url ?? (organizer?.id ? `/o/${organizer.id}` : null);
  const contactOrganizerUrl = organizerUrl ? withContactOrganizerQuery(organizerUrl, eventId) : null;
  const passwordErrorMessage =
    passwordErrorKey === 'wrongPassword'
      ? formatMessage(WRONG_PASSWORD_MESSAGE)
      : passwordErrorKey === 'generic'
        ? formatMessage(GENERIC_PASSWORD_ERROR_MESSAGE)
        : null;

  return (
    <div className={styles.gate} data-testid="protected-event-gate">
      <div className={styles.background} aria-hidden>
        {backgroundImageUrl && (
          <Image
            src={backgroundImageUrl}
            alt=""
            width={960}
            height={540}
            quality={45}
            loading="eager"
            className={styles.backgroundImage}
            data-testid="protected-gate-background-image"
          />
        )}
      </div>
      <div className={styles.card}>
        <div className={styles.cardContent}>
          {isEnded && (
            <div className={styles.endedBadge}>
              <DisplayStatusProvider salesStatusData={{ salesStatus: 'sales_ended' }} eventStatus="ended">
                <SignalBadge />
              </DisplayStatusProvider>
            </div>
          )}

          <header className={styles.header}>
            <div className={styles.privateRow}>
              <LockLineIcon size="small" className={styles.lockIcon} aria-hidden />
              <Typography variant="body-md" as="p" className={styles.privateLabel}>
                <FormattedMessage
                  id="Private event"
                  defaultMessage="Private event"
                  description="Label on password gate"
                />
              </Typography>
            </div>
            <Typography variant="heading-md" as="h1" className={styles.title}>
              {name}
            </Typography>
            {organizer?.name && (
              <OrganizerFollowStatusProvider organizer={organizer as Organizer}>
                <OrganizerProfileProvider organizer={organizer as Organizer}>
                  <div className={styles.organizerInfoWrapper}>
                    <OrganizerInfo
                      organizer={organizer as Organizer}
                      showFollowButton={false}
                      organizerLinkHref={organizerUrl ?? undefined}
                      suppressDefaultLinkFallback
                    />
                  </div>
                </OrganizerProfileProvider>
              </OrganizerFollowStatusProvider>
            )}
            <div className={styles.meta}>
              <EventDateTime
                startDate={startDate}
                endDate={endDate}
                locale={locale?.replace('_', '-') ?? 'en-US'}
                isOnlineEvent={false}
                isRecurringEvent={false}
                hideStartDate={hideStartDate}
              />
            </div>
          </header>

          <form onSubmit={handleSubmit} className={styles.form} noValidate>
            <Stack space="spacing-md">
              <div className={styles.passwordField}>
                <TextField
                  label={passwordErrorMessage ?? formatMessage(PASSWORD_LABEL)}
                  type="password"
                  value={password}
                  onChange={(value) => {
                    setPassword(value);
                    if (passwordErrorKey) setPasswordErrorKey(null);
                  }}
                  hasError={!!passwordErrorMessage}
                  autoComplete="current-password"
                  className={passwordErrorMessage ? styles.passwordError : undefined}
                  aria-invalid={!!passwordErrorMessage}
                />
              </div>
              <div className={styles.actions}>
                <LoadingButton
                  type="submit"
                  variant="primary"
                  isLoading={isSubmitting}
                  disabled={!password.trim() || isSubmitting}
                  className={styles.submitButton}
                >
                  <FormattedMessage
                    id="Access event page"
                    defaultMessage="Access event page"
                    description="Submit button on password gate"
                  />
                </LoadingButton>
                {isEnded && contactOrganizerUrl && (
                  <a href={contactOrganizerUrl} className={styles.contactButton}>
                    <FormattedMessage
                      id="Contact organizer"
                      defaultMessage="Contact organizer"
                      description="Link to organizer contact on password gate"
                    />
                  </a>
                )}
              </div>
            </Stack>
          </form>
        </div>
      </div>
    </div>
  );
}
